/**
 * AnchorPicker — H1 fix: extracted shared anchor logic from GoalFormDialog and OutcomeFormDialog.
 *
 * Both dialogs had the same three-mode toggle (Under Vision / Under [Goal|Outcome] / None)
 * plus a conditional dropdown for the specific parent entity. This component deduplicates that logic.
 *
 * mode='goal'    → parent options: Vision or Outcome dropdown (uses useRorosOutcomes)
 * mode='outcome' → parent options: Vision or Goal dropdown (uses useRorosGoals)
 */

import React, { useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { t } from '@/lib/i18n';
import { useRorosOutcomes } from '@/hooks/roros/artifacts';
import { useRorosGoals } from '@/hooks/roros/goals';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AnchorPickerProps {
  /** 'goal' → can anchor under Vision or Outcome; 'outcome' → under Vision or Goal */
  mode: 'goal' | 'outcome';
  /** Currently selected parent type */
  parentType: 'vision' | 'goal' | 'outcome' | null;
  /** UUID of the currently selected parent entity (Outcome or Goal) */
  parentId: string | null;
  /** Called when either parentType or parentId changes */
  onChange: (parentType: 'vision' | 'goal' | 'outcome' | null, parentId: string | null) => void;
  /** Domain UUID — scopes the parent entity dropdown */
  domainId: string;
  /** Validation error shown below the picker */
  error?: string;
  /**
   * Prefix for `data-testid` attributes (and the linked `htmlFor`/`id` pair on
   * the entity select). Defaults to `'anchor'`.
   *
   * ⚠️ Uniqueness: this value is combined with `mode` to produce DOM `id`
   * attributes (e.g. `anchor-entity-select-goal`). If two AnchorPicker
   * instances with the **same** `testIdPrefix` and `mode` appear in the same
   * document, duplicate `id` values will break `htmlFor` label association.
   * Ensure the `testIdPrefix` + `mode` combination is unique per page.
   */
  testIdPrefix?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AnchorPicker({
  mode,
  parentType,
  parentId,
  onChange,
  domainId,
  error,
  testIdPrefix = 'anchor',
}: AnchorPickerProps) {
  // For mode='goal': parent entity is an Outcome
  const outcomesQuery = useRorosOutcomes(
    mode === 'goal' ? (domainId || '') : '',
    mode === 'goal' ? { status: 'active' } : undefined,
  );

  // For mode='outcome': parent entity is a Goal
  const goalsQuery = useRorosGoals(
    mode === 'outcome' ? { status: 'active', domainId: domainId || undefined } : undefined,  // A1
  );

  // ── Entity dropdown options ─────────────────────────────────────────────────

  const entityOptions = useMemo(() => {
    if (mode === 'goal') {
      const outcomes = (outcomesQuery.data ?? []) as Array<{ id: string; title: string }>;
      return [
        { value: '__none__', label: t('Select an outcome…') },
        ...outcomes.map((o) => ({ value: o.id, label: o.title })),
      ];
    }
    // mode='outcome': goals
    const goals = (goalsQuery.data?.data ?? goalsQuery.data ?? []) as Array<{ id: string; title: string }>;
    return [
      { value: '__none__', label: t('Select a goal…') },
      ...goals.map((g) => ({ value: g.id, label: g.title })),
    ];
  }, [mode, outcomesQuery.data, goalsQuery.data]);

  // ── Button labels / entity type label ──────────────────────────────────────

  const entityButtonLabel = mode === 'goal' ? t('Under an Outcome') : t('Under a Goal');
  const entityParentType = mode === 'goal' ? 'outcome' : 'goal';
  const hasEntity = parentType === entityParentType;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleVision = () => onChange('vision', null);
  const handleEntity = () => onChange(entityParentType as 'goal' | 'outcome', parentId);
  const handleNone = () => onChange(null, null);
  const handleEntitySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    onChange(entityParentType as 'goal' | 'outcome', val === '__none__' ? null : val);
  };

  return (
    <div>
      {/* Three-mode toggle */}
      <div className="flex gap-2 mb-2">
        <Button
          type="button"
          variant={parentType === 'vision' ? 'primary' : 'outline'}
          size="sm"
          onClick={handleVision}
          data-testid={`${testIdPrefix}-vision-${mode}`}
        >
          {t('Under Vision')}
        </Button>
        <Button
          type="button"
          variant={hasEntity ? 'primary' : 'outline'}
          size="sm"
          onClick={handleEntity}
          data-testid={`${testIdPrefix}-entity-${mode}`}
        >
          {entityButtonLabel}
        </Button>
        <Button
          type="button"
          variant={parentType === null ? 'primary' : 'outline'}
          size="sm"
          onClick={handleNone}
          data-testid={`${testIdPrefix}-none-${mode}`}
        >
          {t('None')}
        </Button>
      </div>

      {/* Entity dropdown (shown only when entity type is selected) */}
      {hasEntity && (
        <div className="mt-2">
          <label
            className="text-sm text-muted-foreground block mb-1"
            htmlFor={`${testIdPrefix}-entity-select-${mode}`}
          >
            {mode === 'goal' ? t('Parent Outcome') : t('Parent Goal')}
          </label>
          <Select
            id={`${testIdPrefix}-entity-select-${mode}`}
            value={parentId || '__none__'}
            onChange={handleEntitySelect}
            options={entityOptions}
            data-testid={`${testIdPrefix}-entity-select-${mode}`}
          />
          {entityOptions.length <= 1 && (
            <p className="text-xs text-muted-foreground mt-1">
              {mode === 'goal'
                ? t('No active outcomes in this domain yet.')
                : t('No active goals in this domain yet.')}
            </p>
          )}
        </div>
      )}

      {/* Contextual hint */}
      {parentType === 'vision' && (
        <p className="text-xs text-muted-foreground mt-1">
          {t('This item is anchored to the organisation Vision.')}
        </p>
      )}
      {parentType === null && (
        <p className="text-xs text-muted-foreground mt-1">
          {t('No anchor — this item is standalone (a warning will appear on save).')}
        </p>
      )}

      {/* Validation error */}
      {error && (
        <p className="text-xs text-destructive mt-1">{error}</p>
      )}
    </div>
  );
}
